import { SvgIconProps } from '@material-ui/core';
import { groupBy } from 'lodash';
import Beans from 'src/resources/icons/Beans';
import { productText } from './productTexts';

// Todo implement me
interface ProductPropertyPopup {}

interface ProductBaseProperty<T extends 'text' | 'color' | 'rating' | 'ratio' | 'price'> {
    type: T;
    label: string;
    localizedLabel: string;
    highlight?: boolean;
    localizedTooltip?: string;
    popup?: ProductPropertyPopup;
    /** 12.8: the text block of the label (TPL_…), when it has one. */
    nameKey?: string;
}

export interface ProductTextProperty extends ProductBaseProperty<'text'> {
    localizedText: string;
    /** 12.8: the text block of the value (TPL_…), when it has one. */
    valueKey?: string;
}

export interface ProductColorProperty extends ProductBaseProperty<'color'> {
    value: string;
    localizedName: string;
}

type ProductRatingPropertyIcon = 'bean';

export const ProductRatingPropertyIconMap: Record<ProductRatingPropertyIcon, (props: SvgIconProps) => JSX.Element> = {
    bean: Beans,
};

export interface ProductRatingProperty extends ProductBaseProperty<'rating'> {
    icon: ProductRatingPropertyIcon;
    value: number;
    max: number;
}

interface ProductRatioPropertyValue {
    label: string;
    localizedLabel: string;
    value: number;
}

export interface ProductRatioProperty extends ProductBaseProperty<'ratio'> {
    values: ProductRatioPropertyValue[];
}

export interface ProductPriceProperty extends ProductBaseProperty<'price'> {
    value: number;
    currencyIso: string;
}

export type ProductProperty =
    | ProductTextProperty
    | ProductColorProperty
    | ProductRatingProperty
    | ProductRatioProperty
    | ProductPriceProperty;
/** 12.10/12.8: no raw keys on screen, and every text with a text block registered for the translator mode. */
function shownProperty(property: ProductProperty): ProductProperty {
    const shown = { ...property, localizedLabel: productText(property.localizedLabel, property.nameKey) };
    switch (shown.type) {
        case 'text':
            return { ...shown, localizedText: productText(shown.localizedText, shown.valueKey) };
        case 'color':
            return { ...shown, localizedName: productText(shown.localizedName) };
        case 'ratio':
            return {
                ...shown,
                values: (shown.values ?? []).map(value => ({
                    ...value,
                    localizedLabel: productText(value.localizedLabel, value.label),
                })),
            };
        default:
            return shown;
    }
}

export class ProductProperties {
    public readonly properties: ProductProperty[];

    constructor(properties: any) {
        this.properties = Array.isArray(properties) ? properties.map(shownProperty) : [];
    }

    public get(onlyHighlights?: boolean): ProductProperty[] {
        return this.properties.filter(p => (onlyHighlights ? p.highlight : true));
    }

    public getColor(): ProductColorProperty | undefined {
        return this.properties.find(p => p.type === 'color') as ProductColorProperty;
    }

    public getPrices(): ProductPriceProperty[] {
        return this.properties.filter(p => p.type === 'price') as ProductPriceProperty[];
    }

    // Sums up all price properties grouped by their currency
    public getSummedPrices(label: string = '', localizedLabel: string = ''): ProductPriceProperty[] {
        const pricesByCurrency = groupBy(this.getPrices(), p => p.currencyIso);
        return Object.values(pricesByCurrency).map(g =>
            g.reduce(
                (sum, cur) => {
                    sum.value += cur.value;
                    return sum;
                },
                { currencyIso: g[0].currencyIso, value: 0, label, localizedLabel, type: 'price' }
            )
        ) as ProductPriceProperty[];
    }
}
